import { useCallback, useEffect, useMemo } from 'preact/hooks';
import { Signal, useSignal } from '@preact/signals';
import { MoveDirection } from '@accedo/spatial-nav';
import { KeyCodes, sendVirtualKeyEvent, Mouse, VirtualKeyboardEvent } from '@accedo/virtual-key';
import { noop } from '@accedo/general-utils';
import { createCursorVisibilityEvent, createWheelEventListener } from '##/utils/mouseEvents';

export default function useMouse(): {
  isMouseEnabled: Signal<boolean>;
  triggerArrowClick: (direction: MoveDirection) => void;
} {
  const isMouseEnabled = useSignal(false);

  const triggerArrowClick = useCallback(
    (direction: MoveDirection): void => {
      if (!isMouseEnabled.peek()) {
        return;
      }
      sendVirtualKeyEvent({ virtualKey: direction, source: Mouse } as VirtualKeyboardEvent);
    },
    [isMouseEnabled],
  );

  const handleWheel = useCallback(
    (event: WheelEvent): void => {
      if (!isMouseEnabled.peek()) {
        return;
      }

      if (event.deltaY < 0) {
        sendVirtualKeyEvent({ virtualKey: KeyCodes.vKey.UP, source: Mouse } as VirtualKeyboardEvent);
      }
      if (event.deltaY > 0) {
        sendVirtualKeyEvent({ virtualKey: KeyCodes.vKey.DOWN, source: Mouse } as VirtualKeyboardEvent);
      }
    },
    [isMouseEnabled],
  );

  const onCursorStateChange = useCallback(
    (event: Event): void => {
      const { visibility } = (
        event as CustomEvent<{
          visibility: boolean;
        }>
      )?.detail ?? { visibility: false };
      isMouseEnabled.value = Boolean(visibility);
    },
    [isMouseEnabled],
  );

  /**
   * This useEffect adds support for mouse such as LG Magic Remote.
   * It also adds support for mouse in workstation if you add
   * the configuration `mouseEnabled: true` in the webpack config.
   */
  useEffect(() => {
    // Do not add mouse event listeners if mouse support is not enabled
    const mouseEnabled = Boolean(process.env.MOUSE_ENABLED);

    if (!mouseEnabled) {
      return noop;
    }

    const unsubscribeWheel = createWheelEventListener(handleWheel);
    const unsubscribeCursorStateChange = createCursorVisibilityEvent(onCursorStateChange);

    return () => {
      unsubscribeWheel();
      unsubscribeCursorStateChange();
    };
  }, [handleWheel, onCursorStateChange]);

  return useMemo(
    () => ({
      isMouseEnabled,
      triggerArrowClick,
    }),
    [isMouseEnabled, triggerArrowClick],
  );
}
